import { RoadmapTask } from "@models";
import { CheckOutlined, MoreVert } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { fillRouteWithSlugs } from "@utils";
import type { Dayjs } from "dayjs";
import { Flower } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GenericButton from "src/components/GenericButton/GenericButton";
import { useSnackbar } from "src/components/Snackbar/useSnackbar";
import { DeadlinePicker } from "src/components/Tasks/DeadlinePicker";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import type { AgentThread } from "src/stores/agent";
import { HandOverConfirmModal } from "./HandOverConfirmModal";

const shouldShowHandOverModal = (): boolean => {
  return localStorage.getItem("handOverToAgentModalDismissed") !== "true";
};

export const TaskMoreOptions = ({
  deviceId,
  handleSubmit,
  task,
  handleOpenOptions,
  handleCloseOptions,
  thread,
  showDelegateAction = true,
  showDeadlineAction = false,
  dueDate = null,
  onDueDateChange,
  onDueDateClear,
  isDeadlineDisabled = false,
  disabledTooltip,
}: {
  deviceId: string;
  handleSubmit: (
    e: React.MouseEvent,
    deviceId: string,
    orgId: string,
    task: RoadmapTask
  ) => Promise<AgentThread>;
  task: RoadmapTask;
  handleOpenOptions: () => Promise<void>;
  handleCloseOptions: () => void;
  thread: AgentThread | null;
  showDelegateAction?: boolean;
  showDeadlineAction?: boolean;
  dueDate?: Dayjs | null;
  onDueDateChange?: (newDate: Dayjs | null) => void;
  onDueDateClear?: () => void;
  isDeadlineDisabled?: boolean;
  disabledTooltip?: string;
}) => {
  const { orgId = "" } = useParams();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showHandOverModal, setShowHandOverModal] = useState(false);
  const moreOptionsOpen = Boolean(anchorEl);

  const [isLoadingThread, setIsLoadingThread] = useState(
    thread?.status === "busy"
  );

  useEffect(() => {
    setIsLoadingThread(thread?.status === "busy");
  }, [thread]);

  const handleClickMoreOptions = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    const anchor = event.currentTarget;
    if (showDelegateAction) {
      try {
        await handleOpenOptions();
      } catch {
        setAnchorEl(null);
        handleCloseOptions();
        showSnackbar("Could not load the agent run. Try again.");
        return;
      }
    }
    setAnchorEl(anchor);
  };

  const handleCloseMoreOptions = () => {
    setAnchorEl(null);
    handleCloseOptions();
  };

  const handleCreateRun = async (
    e: React.MouseEvent,
    saveDontShowAgain?: boolean
  ) => {
    handleCloseMoreOptions();

    if (saveDontShowAgain) {
      localStorage.setItem("handOverToAgentModalDismissed", "true");
    }

    const newThread = await handleSubmit(e, deviceId, orgId, task);

    if (!showDelegateAction) {
      setIsLoadingThread(false);
      return;
    }

    if (newThread?.thread_id) {
      const runUrl =
        fillRouteWithSlugs(ROUTES.TERMINAL, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
        }) +
        "?agentThreadId=" +
        newThread.thread_id +
        "&joinStream=true";

      const actionButton = (
        <span
          onClick={() => navigate(runUrl)}
          style={{
            cursor: "pointer",
            fontSize: "12px",
            textDecoration: "underline",
            paddingRight: "10px",
          }}
        >
          Go to Terminal
        </span>
      );

      showSnackbar(
        "Run created! Follow the activity in the terminal.",
        actionButton
      );
    }

    setIsLoadingThread(false);
  };

  const handleMenuItemClick = async (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>
  ) => {
    e.stopPropagation();
    handleCloseMoreOptions();

    if (thread?.thread_id) {
      navigate(
        fillRouteWithSlugs(ROUTES.TERMINAL, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
        }) +
          "?agentThreadId=" +
          thread.thread_id
      );
    } else {
      if (shouldShowHandOverModal()) {
        setShowHandOverModal(true);
      } else {
        await handleCreateRun(e);
      }
    }
  };

  const handleHandOverConfirm = async (
    e: React.MouseEvent,
    dontShowAgain: boolean
  ) => {
    setShowHandOverModal(false);
    await handleCreateRun(e, dontShowAgain);
  };

  const handleHandOverCancel = (dontShowAgain: boolean) => {
    setShowHandOverModal(false);

    if (dontShowAgain) {
      localStorage.setItem("handOverToAgentModalDismissed", "true");
    }
  };

  const hasCompletedThread =
    thread && thread?.status !== "busy" && !isLoadingThread;
  const hasNoThread = !thread && !isLoadingThread;
  const dueDateFormat = "MM/DD/YYYY";
  const dueDatePlaceholder = "No due date";
  const dueDateInputSize =
    (dueDate
      ? dueDate.format(dueDateFormat).length
      : dueDatePlaceholder.length) + 1;
  const compactDateSx = {
    width: "auto",
    minWidth: 0,
    "& .MuiInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottom: "none",
    },
    "& .MuiInputBase-root": {
      width: "auto",
      minWidth: 0,
      px: 1,
      py: 0.5,
      borderRadius: 1,
      display: "inline-flex",
      "&:hover": {
        backgroundColor: "action.hover",
      },
    },
    "& .MuiInputBase-input": {
      lineHeight: "20px",
      p: 0,
      width: "auto",
      minWidth: 0,
    },
    "& .MuiInputAdornment-root": {
      ml: 0.5,
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 150ms ease",
    },
    "& .MuiInputBase-root:hover .MuiInputAdornment-root, & .MuiInputBase-root.Mui-focused .MuiInputAdornment-root":
      {
        opacity: 1,
        pointerEvents: "auto",
      },
  } as const;

  return (
    <>
      <GenericButton
        dataTestId="task-more-options-button"
        ariacontrols={
          moreOptionsOpen ? `task-more-options-menu-${task.taskKey}` : undefined
        }
        ariahaspopup={true}
        ariaexpanded={moreOptionsOpen ? true : undefined}
        id={`task-more-options-button-${task.taskKey}`}
        onClick={handleClickMoreOptions}
        hideTooltip
      >
        <MoreVert />
      </GenericButton>
      <Menu
        id={`task-more-options-menu-${task.taskKey}`}
        anchorEl={anchorEl}
        open={moreOptionsOpen}
        onClose={handleCloseMoreOptions}
        MenuListProps={{
          "aria-labelledby": `task-more-options-button-${task.taskKey}`,
        }}
        disablePortal
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        BackdropProps={{
          onClick: (event: React.MouseEvent) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            handleCloseMoreOptions();
          },
          onMouseDown: (event: React.MouseEvent) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
          },
        }}
      >
        {showDeadlineAction && (
          <>
            <ListSubheader>Deadline</ListSubheader>
            <Box
              sx={{ px: 1 }}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <Tooltip
                title={isDeadlineDisabled ? (disabledTooltip ?? "") : ""}
                disableHoverListener={!isDeadlineDisabled}
                placement="top"
              >
                <Box
                  component="span"
                  data-testid="roadmap-task-deadline-tooltip-trigger"
                >
                  <DeadlinePicker
                    value={dueDate}
                    disabled={isDeadlineDisabled}
                    onChange={(newDate) => {
                      onDueDateChange?.(newDate);
                      handleCloseMoreOptions();
                    }}
                    onClear={
                      onDueDateClear
                        ? () => {
                            onDueDateClear();
                            handleCloseMoreOptions();
                          }
                        : undefined
                    }
                    ariaLabel="Roadmap task due date"
                    format={dueDateFormat}
                    placeholder={dueDatePlaceholder}
                    textFieldProps={{
                      variant: "standard",
                      hiddenLabel: true,
                      inputProps: {
                        size: Math.max(dueDateInputSize, 1),
                      },
                      InputProps: {
                        disableUnderline: true,
                      },
                      onClick: (event) => event.stopPropagation(),
                      sx: compactDateSx,
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>
          </>
        )}

        {showDelegateAction && (
          <>
            <ListSubheader>More Actions</ListSubheader>
            <MenuItem
              className="flex items-center gap-2"
              onClick={handleMenuItemClick}
            >
              {isLoadingThread && (
                <>
                  <div className="flex items-center">
                    <CircularProgress size={13} color="inherit" />
                  </div>
                  Follow activity
                </>
              )}
              {hasCompletedThread && (
                <>
                  <div className="flex items-center">
                    <CheckOutlined color="success" />
                  </div>
                  Go to Agent
                </>
              )}
              {hasNoThread && (
                <>
                  <Flower size={21} />
                  Delegate to Qara
                  <Chip label="Beta" size="tiny" />
                </>
              )}
            </MenuItem>
          </>
        )}
      </Menu>

      {showDelegateAction && (
        <HandOverConfirmModal
          isOpen={showHandOverModal}
          onClose={handleHandOverCancel}
          onConfirm={handleHandOverConfirm}
        />
      )}
    </>
  );
};
